import moment from 'moment';
import { ScheduleListItem } from '../ManageSchedules/hooks/useGetScheduleList';

export type Frequency = 'hourly' | 'daily' | 'weekly';

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export interface ScheduleFormErrors {
  time?: string;
  day?: string;
  subscribers?: string;
}

export interface ScheduleEditData {
  schedule_id: number;
  frequency: Frequency;
  run_time: string | null;
  week_day: string | null;
  subscribers: string[];
  is_internal?: boolean;
}

export interface ScheduleAnalysisDrawerProps {
  open: boolean;
  onClose: () => void;
  analysisItem: { id: number; query: string } | null;
  mode?: 'create' | 'edit';
  scheduleData?: ScheduleEditData;
  onRequestOpenManageSchedules?: (opts?: { scheduleId?: number }) => void;
}

/**
 * Generates time options in 15-minute intervals from 00:00 to 23:59.
 */
export const generateTimeOptions = (): { label: string; value: string }[] => {
  const options: { label: string; value: string }[] = [];
  const intervalMinutes = 15;

  for (let h = 0; h < 24; h++) {
    for (let t = 0; t < 60 / intervalMinutes; t++) {
      const minute = `${t * intervalMinutes}`.padStart(2, '0');
      const val = `${`${h}`.padStart(2, '0')}:${minute}`;
      options.push({ label: val, value: val });
    }
  }

  // Include 23:59 as the final option
  options.push({ label: '23:59', value: '23:59' });

  return options;
};

export interface MatchSignature {
  analysisId: number;
  frequency: Frequency;
  time: string;
  weekDay: DayOfWeek | null;
}

export const normalizeTimeToHHmm = (time: string | null): string | null => {
  if (!time) return null;
  return time.substring(0, 5);
};

export const normalizeWeekDay = (
  frequency: Frequency,
  weekDay: string | DayOfWeek | null,
): DayOfWeek | null => {
  if (frequency !== 'weekly' || !weekDay) {
    return null;
  }

  const normalizedWeekDay = DAYS_OF_WEEK.find(
    (day) => day.toLowerCase() === `${weekDay}`.toLowerCase(),
  );
  return normalizedWeekDay || null;
};

export const parseSubscriberEmails = (rawEmails: string): string[] => {
  const uniqueEmails = new Set<string>();
  return rawEmails
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)
    .filter((email) => {
      const normalizedEmail = email.toLowerCase();
      if (uniqueEmails.has(normalizedEmail)) {
        return false;
      }
      uniqueEmails.add(normalizedEmail);
      return true;
    });
};

export const isExactMatch = (schedule: ScheduleListItem, signature: MatchSignature): boolean => {
  return (
    schedule.saved_analysis_id === signature.analysisId &&
    schedule.frequency === signature.frequency &&
    normalizeTimeToHHmm(schedule.run_time) === signature.time &&
    normalizeWeekDay(schedule.frequency, schedule.week_day) === signature.weekDay
  );
};

export const hasSameAnalysisAndFrequency = (
  schedule: ScheduleListItem,
  signature: MatchSignature,
): boolean =>
  schedule.saved_analysis_id === signature.analysisId && schedule.frequency === signature.frequency;

export const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Computes the next_run datetime string in UTC, formatted as 'YYYY-MM-DD HH:mm:ss' to be compatible with MySQL DATETIME type.
 */
export const computeNextRun = (
  frequency: Frequency,
  time: string,
  day: DayOfWeek | null,
): string => {
  const [hour, mins] = time.split(':').map(Number);

  if (frequency === 'hourly' || frequency === 'daily') {
    const nextRun = moment().hours(hour).minutes(mins).seconds(0).milliseconds(0);
    // If the computed time is in the past, advance to tomorrow
    if (nextRun.isBefore(moment())) {
      nextRun.add(1, 'day');
    }
    return nextRun.utc().format(DATE_TIME_FORMAT);
  }

  // Weekly: find the next occurrence of the selected day
  if (day) {
    const dayIndex = DAYS_OF_WEEK.indexOf(day);
    // moment isoWeekday: 1=Monday ... 7=Sunday
    const targetIsoDay = dayIndex + 1;
    const nextRun = moment()
      .isoWeekday(targetIsoDay)
      .hours(hour)
      .minutes(mins)
      .seconds(0)
      .milliseconds(0);
    // If the resulting date is in the past, go to next week
    if (nextRun.isBefore(moment())) {
      nextRun.add(1, 'week');
    }
    return nextRun.utc().format(DATE_TIME_FORMAT);
  }

  return moment().utc().format(DATE_TIME_FORMAT);
};
